import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toastService from '../services/toastService';
import { useAuth } from '../context/AuthContext';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarChart, faFileImport, faFileInvoice, faFileUpload, faGear, faGlobe, faGlobeOceania, faHeadset, faHexagonNodes, faPlus, faSearch, faTableColumns, faUser, faCheck, faTimes, faChevronCircleLeft, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { faEdit, faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { listFiles, uploadFile } from '../services/fileUploadService';
import { getAllEntitiesByFilter } from '../services/entityService';
import { getKnowledgeBases } from '../services/knowledgeBaseService';
import { getAllConnectorsByFilter } from '../services/connectorService';
// import { getDataSources, updateDataSource, createDataSource } from '../services/dataSourceService';
import { createChatForKb, createMessageForChat, getAllChatsByFilter, getAllMessagesByFilter, getDocMetaDataWithFilters, ingestFileToKb, searchKnowledgeBase } from '../services/kbQueryService';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  ContentRow,
  Card,
  CardTitle,
  CardContent,
  ListItemTime,
  Button,
  Input,
  SearchInput,
  ListItemStatus,
  FloatingCard,
  AgentHeader,
  AgentAvatar,
  AgentInfo,
  AgentName,
  AgentType,
  List,
  ListItem,
  ListItemIcon,
  Table,
  ChatButtons,
  ListItemContent,
  ListItemTitle,
  ListItemDescription,
  formatDate,
  formatBytes,
  fadeIn,
  ListItemMeta,
  FlexLayout,
  Flex1ThirdLayout,
  Flex2ThirdLayout,
  SubSectionTitle,
  StrongOrbi,
  Divider,
  TableCell,
  FormRowFull,
  CollapsibleSidebar,
  ChatInput,
  ChatWrapper,
  ChatScrollContainer,
  ChatUserCard,
  ChatSystemCard,
  ChatBottom,
  useGoBack,
  TableHeader,
  TableRow,
  StatusBadge,
  QueryBadge,
  TooltipWrapper,
  Tooltip
} from '../styles/PageStyles';

// Add CSS for the slideUp animation
const slideUpKeyframes = `
@keyframes slideUp {
  from {
    transform: translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
`;

// Create a style element and append it to the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.appendChild(document.createTextNode(slideUpKeyframes));
  document.head.appendChild(style);
}

const KnowledgeBaseQuery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { knowledgebaseId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [knowledgeBaseDetail, setKnowledgebaseDetail] = useState();
  const [connector, setConnector] = useState();
  // const [dataSource, setDataSource] = useState([]);
  const [files, setFiles] = useState([]);
  const [fileCount, setFileCount] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [chatMode, setChatMode] = useState(null);
  const [sideBarCollapsed, setSideBarCollapsed] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageList, setMessageList] = useState(null);
  const [chatList, setChatList] = useState([]);
  const [maxFiles, setMaxFiles] = useState(10);
  const [maxSize, setMaxSize] = useState(10 * 1024 * 1024); // 10 MB
  const [fileTypes, setFileTypes] = useState({
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg']
  });
  const [transcribedText, setTranscribedText] = useState('');
  const [queryContext, setQueryContext] = useState('query');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchModalTerm, setSearchModalTerm] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // For the documents uploaded for this KB
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [documentList, setDocumentList] = useState([]);
  const [polling, setPolling] = useState(false);
  const [showAttachmentPanel, setShowAttachmentPanel] = useState(false);


  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const textareaRef2 = useRef(null);

  // // For COnnector corresponding to DS
  // useEffect(() => {
  //   const fetchConnectors = async () => {
  //     try {
  //       let filter = {}
  //       const response = await getAllConnectorsByFilter(filter, {});
  //       setConnector(response || []);
  //     } catch (error) {
  //       console.error('Error fetching users:', error);
  //     }
  //   };
  //   fetchConnectors();
  // }, [user]);

  // For KNowledgeBase Current - only runs once when component mounts or when knowledgebaseId changes
  useEffect(() => {
    const fetchKnowledgebaseByCode = async () => {
      try {
        const parsedKnowledgebaseId = (knowledgebaseId);

        let filter = {
          "code": parsedKnowledgebaseId
        }
        // console.log("Fetching knowledge base data...");
        const response = await getKnowledgeBases(filter, {});
        setKnowledgebaseDetail({
          ...response[0],
          pii_flag: response[0]?.pii_flag ?? false,
        });

      } catch (error) {
        console.error('Error fetching knowledge base:', error);
      }
    };
    if (knowledgebaseId) {
      fetchKnowledgebaseByCode();
    } else {
      navigate(`/knowledge-base/${knowledgebaseId}`);
    }
    // Only re-run if knowledgebaseId changes, not on every user change
  }, [knowledgebaseId, navigate]);

  // Define fetchFiles with useCallback, using stable kbCode parameter instead of the whole object
  const fetchFiles = React.useCallback(async (kbCode) => {
    try {
      if (kbCode) {
        console.log("Fetching files for knowledge base...");
        // Call API to fetch files using the knowledgeBaseDetail code
        const response = await listFiles(kbCode);
        setUploadedFiles(response || []);
        setFileCount(response.length)
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  }, []); // No dependencies since we're passing kbCode as a parameter

  // Files OF KB - runs only when knowledgeBaseDetail.code changes or fileCount is updated
  useEffect(() => {
    if (knowledgeBaseDetail?.code) {
      fetchFiles(knowledgeBaseDetail.code);
    }
  }, [fetchFiles, knowledgeBaseDetail?.code, fileCount]); // Only depend on the code property and fileCount
  const fetchDocumentList = async () => {
    try {
      const response = await getDocMetaDataWithFilters({ knowledge_base: knowledgeBaseDetail.code });
      setDocumentList(response || []);
      fetchFiles();
      pollDocumentStatus();
    } catch (error) {
      console.error('Error fetching document list:', error);
    }
  };
  const pollDocumentStatus = async (flag = false) => {
    setPolling(flag);
    try {
      let allCompleted = false;
      while (!allCompleted) {
        const response = await getDocMetaDataWithFilters({ knowledge_base: knowledgeBaseDetail.code });
        setDocumentList(response || []);
        allCompleted = response.every(
          (doc) => doc.processing_status === 'COMPLETED' || doc.processing_status === 'ERROR'
        );
        if (!allCompleted) {
          await new Promise((resolve) => setTimeout(resolve, 10000)); // Poll every 5 seconds
        }
      }
    } catch (error) {
      console.error('Error polling document status:', error);
    } finally {
      setPolling(false);
    }
  };
  // For Datasource
  // useEffect(() => {
  //   const fetchDataSourcesWithConnectors = async () => {
  //     try {
  //       if (Array.isArray(knowledgeBaseDetail?.data_source) && knowledgeBaseDetail.data_source.length > 0) {
  //         const dataSourceResponses = await Promise.all(
  //           knowledgeBaseDetail.data_source.map((dataSourceName) => {
  //             const filter = { code: dataSourceName, status: 'ACTIVE' };
  //             return getDataSources(filter, {});
  //           })
  //         );

  //         // Extract the first record from each data source response
  //         const fetchedDataSources = dataSourceResponses.map((response) => response[0]).filter(Boolean);

  //         // Fetch connectors for each data source and add connector details
  //         const updatedDataSources = await Promise.all(
  //           fetchedDataSources.map(async (ds) => {
  //             const connectorResponse = connector.filter((conn) => conn.code === ds.connector);
  //             const connectorDetails = connectorResponse[0] ?? []; // Get the first connector
  //             return { ...ds, connectorDetails }; // Add connector details to the data source
  //           })
  //         );

  //         // Update the dataSource state with the enriched data sources
  //         setDataSource(updatedDataSources);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching data sources or connectors:', error);
  //     }
  //   };

  //   fetchDataSourcesWithConnectors();
  // }, [knowledgeBaseDetail?.data_source, connector]);

  // Chats - define fetchChats with useCallback for better performance
  const fetchChats = React.useCallback(async (kbCode) => {
    try {
      if (kbCode) {
        console.log("Fetching chats for knowledge base...");
        let filter = {
          kb: kbCode
        }
        // Call API to fetch chats using the code
        const response = await getAllChatsByFilter(filter);
        if (response && response.length > 0) {
          setChatList(response || []);
        } else {
          setSideBarCollapsed(true)
        }
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  }, []);

  useEffect(() => {
    if (knowledgeBaseDetail?.code) {
      fetchChats(knowledgeBaseDetail.code);
    }
  }, [knowledgeBaseDetail?.code, fetchChats]); 


  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messageList]); 
  
  const adjustTextareaHeight = (element) => {
    if (!element) return;
    
    element.style.height = 'auto';
    
    const newHeight = Math.min(element.scrollHeight, 50); // Max height
    element.style.height = `${newHeight}px`;
    
    element.style.overflowY = element.scrollHeight > 50 ? 'auto' : 'hidden';
  };
  
  // Adjust height when text changes
  useEffect(() => {
    if (textareaRef.current) {
      adjustTextareaHeight(textareaRef.current);
    }
    if (textareaRef2.current) {
      adjustTextareaHeight(textareaRef2.current);
    }
  }, [transcribedText]);


  // Add new state for uploaded files with additional metadata
  const [uploadedFileDetails, setUploadedFileDetails] = useState([]);

  // File Upload
  const onDrop = async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach((file) => {
        if (file.errors.some((err) => err.code === 'file-too-large')) {
          toastService.error(`File "${file.file.name}" exceeds the maximum size of ${maxSize / (1024 * 1024)} MB.`);
        }
        if (file.errors.some((err) => err.code === 'file-invalid-type')) {
          const allowedTypes = Object.keys(fileTypes).map(type => type.split('/')[1]).join(', ');
          toastService.error(`File "${file.file.name}" is not an allowed type. Allowed types: ${allowedTypes}`);
        }
      });
    }

    // No accepted files
    if (acceptedFiles.length === 0) {
      return;
    }

    // Check if knowledge base details are available
    if (!knowledgeBaseDetail || !knowledgeBaseDetail.code) {
      toastService.error("Knowledge base information is missing. Please reload the page.");
      return;
    }

    // Check if adding new files exceeds the maxFiles limit
    if (uploadedFileDetails.length + acceptedFiles.length > maxFiles) {
      toastService.error(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    try {
      // Show loading toast
      toastService.info(`Uploading ${acceptedFiles.length} files...`);
      
      // Upload each file but don't ingest yet
      const newUploadedFiles = [];
      for (const file of acceptedFiles) {
        try {
          const uploadResponse = await handleUpload(file);
          if (uploadResponse && uploadResponse.data && uploadResponse.data.url) {
            newUploadedFiles.push({
              url: uploadResponse.data.url,
              name: file.name,
              size: file.size,
              type: file.type
            });
            toastService.success(`File "${file.name}" uploaded successfully`);
          } else {
            toastService.error(`Failed to upload file "${file.name}". Response format incorrect.`);
            console.error('Upload response format incorrect:', uploadResponse);
          }
        } catch (error) {
          toastService.error(`Failed to upload file "${file.name}": ${error.message || "Unknown error"}`);
          console.error(`Error uploading file ${file.name}:`, error);
        }
      }
      
      if (newUploadedFiles.length > 0) {
        // Update the files state with detailed information
        setUploadedFileDetails(prevFiles => [...prevFiles, ...newUploadedFiles]);
        
        // Set chat mode
        setChatMode('chat');
      } else {
        toastService.error("No files were successfully uploaded.");
      }
    } catch (error) {
      console.error('Error in file upload process:', error);
      toastService.error(`Error uploading files: ${error.message || "Unknown error"}`);
    }
  };

  // Function to handle the ingestion process
  const handleIngestFiles = async () => {
    if (uploadedFileDetails.length === 0) {
      // If no files are uploaded, check if there's text before submitting
      if (transcribedText.trim()) {
        handleSubmitQuery();
      }
      return;
    }

    try {
      // Extract just the URLs for ingestion
      const fileUrls = uploadedFileDetails.map(file => file.url);
      if (!fileUrls.length || fileUrls.some(url => !url)) {
        toastService.error("Some file URLs are missing. Please try uploading again.");
        return;
      }
      
      // Get the file names for display in toast messages
      const fileNames = uploadedFileDetails.map(file => file.name).join(", ");
      toastService.info(`Processing files: ${fileNames}`);
      
      // If we have a selected chat, we could update it with file information
      // This is commented out since we're not using it currently but keeping for future reference
      /*
      const fileList = uploadedFileDetails.map(file => file.name).join(", ");
      const updatedChat = {
        ...selectedChat,
        context: 'document',
        title: `${fileList}: ${new Date().toLocaleString()}`,
        document_list: fileUrls,
        coversations: [
          {
            conv_title: `Uploaded File(s): ${fileList}`,
            source: 'user',
            created_at: new Date().toISOString()
          }, {
            conv_title: `Ingestion Started`,
            source: 'system',
            created_at: new Date().toISOString()
          }
        ]
      };
      */
      
      // Show loading toast
      toastService.info(`Starting ingestion of ${fileUrls.length} files...`);
      
      // Ingest files to knowledge base
      const ingestResponse = await ingestFileToKb(
        knowledgeBaseDetail.code, 
        fileUrls, 
        {}, 
        knowledgeBaseDetail.pii_flag ?? true
      );
      
      if (ingestResponse) {
        let tmp = fileCount + 1;
        setFileCount(tmp);
        toastService.success(`Files ingested successfully!`);
        pollDocumentStatus(true);
        
        // Clear the uploaded files after ingestion
        setUploadedFileDetails([]);
        
        // Also process any query text if it exists
        if (transcribedText.trim()) {
          handleSubmitQuery();
        }
      } else {
        toastService.error("Failed to ingest files. Please try again.");
      }
    } catch (error) {
      console.error('Error ingesting files:', error);
      toastService.error(`Error ingesting files: ${error.message || "Unknown error"}`);
    }
  };

  // This function is currently not used but kept for potential future use
  /* 
  const setChatContextandUpdateList = () => {
    // Update selectedChat
    const updatedChat = {
      ...selectedChat,
      context: 'chat',
    };

    // Update chatList with the updated selectedChat
    const updatedChatList = chatList.map((chat) =>
      chat.code === updatedChat.code ? updatedChat : chat
    );

    // Update state
    setSelectedChat(updatedChat);
    setChatList(updatedChatList);
    setChatMode('chat')
  };
  */

  const handleUpload = async (file) => {
    if (file) {
      try {
        const response = await uploadFile(file, knowledgeBaseDetail.code, 'adhoc', new Date().getTime());
        return response;
      } catch (error) {
        console.error('Error uploading file:', error);
        toastService.error(`Error uploading file: ${error.message}`);
      }
    }
  };
  
  // Function to remove a file from the uploaded files list
  const removeFile = (fileUrl) => {
    setUploadedFileDetails(prevFiles => prevFiles.filter(file => file.url !== fileUrl));
  };
  const handleVoiceRecording = () => {
    // Check if the browser supports SpeechRecognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toastService.error('Your browser does not support voice recognition. Please use a supported browser like Chrome or Edge.');
      return;
    }

    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.lang = 'en-US';
    recognition.onresult = (event) => console.log(event.results[0][0].transcript);
    recognition.onerror = (event) => console.error('Error:', event.error);
    recognition.interimResults = false; // Only return final results
    recognition.maxAlternatives = 1;

    // Start recording
    recognition.start();

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setTranscribedText(transcript);
      console.log(transcript)
      // handleSubmitQuery();
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      switch (event.error) {
        case 'not-allowed':
          toastService.error('Microphone access is blocked. Please allow microphone access in your browser settings.');
          break;
        case 'network':
          toastService.error('Network error occurred. Please check your internet connection.');
          break;
        case 'service-not-allowed':
          toastService.error('Speech recognition service is not allowed. Please check your browser settings.');
          break;
        case 'aborted':
          toastService.error('Speech recognition was aborted. Please try again.');
          break;
        case 'audio-capture':
          toastService.error('No microphone was found. Please ensure your microphone is connected and try again.');
          break;
        default:
          toastService.error('An error occurred during voice recognition. Please try again.');
      }
    };

    recognition.onend = () => {
      console.log('Voice recording ended.');
    };
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept: fileTypes,
  });
  const handleSelectedChat = async (chat) => {
    setSelectedChat(chat)
    let filter = {
      kb: knowledgeBaseDetail.code,
      conversation: chat.code
    }
    let response = await getAllMessagesByFilter(filter, { created_at: 'asc' })
    setMessageList(response || [])
    setChatMode('chat');
  }

  const handleSubmitQuery = async () => {
    setChatMode('chat');
    setIsTyping(true);
    let tmpSelectedChat = selectedChat ?? {};
    // If no selectedChat, create a new conversation
    if (!selectedChat) {
      let tmpChat = {
        title: "Temporary Chat",
        company_cd: user.companyCd,
        context: null,
        document_list: [],
        kb: knowledgeBaseDetail.code,
        coversations: [],
        code: "CH-" + new Date().getTime(),
        ingestion_status: null,
        is_selected: false,
        created_at: new Date().toISOString()
      };
      tmpSelectedChat = (tmpChat)
      try {
        // Call API to create a new conversation
        const newChatResponse = await createChatForKb(tmpChat);

        if (newChatResponse) {
          tmpSelectedChat = (newChatResponse)
          setSelectedChat(newChatResponse); // Use the response to set selectedChat
          // tmpTyped.conversation = newChatResponse.conversation_id; // Update conversation code in the message
        }
      } catch (error) {
        console.error("Error creating new chat:", error);
        setIsTyping(false);
        return; // Exit if chat creation fails
      }
    }

    // Add the currently typed message in the MessageList
    let tmpTyped = {
      kb: knowledgeBaseDetail.code,
      to: "agent",
      code: "MSG" + new Date().getTime(),
      from: user.username,
      message: transcribedText,
      company_cd: user.companyCd,
      conversation: tmpSelectedChat?.code || null, // Use selectedChat if available
      created_at: new Date().toISOString(),
    };

    setMessageList((prevMessageList) => [...(prevMessageList || []), tmpTyped]);
    setTranscribedText("");
    // Send message to chat service
    createMessageForChat(tmpTyped)
      .catch(error => console.error("Failed to save message:", error));
    // Scroll to the bottom of the chat container
    setTimeout(() => {
      const chatContainer = document.querySelector(".ChatScrollContainer");
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 100);


    // Call the searchKnowledgeBase API
    let response;
    try {
      response = await searchKnowledgeBase(
        knowledgeBaseDetail.code,
        transcribedText,
        "Doc Name",
        tmpSelectedChat?.code,
        queryContext
      );
      setIsTyping(false);

    } catch (error) {
      console.error("Error searching knowledge base:", error);
      setIsTyping(false);
      return; // Exit if search fails
    }

    // Add the response message to the MessageList
    let tmpResponse = {
      kb: knowledgeBaseDetail.code,
      to: user.username,
      code: "MSG" + new Date().getTime(),
      from: "agent",
      message: response,
      company_cd: user.companyCd,
      conversation: selectedChat?.code || tmpTyped.conversation, // Use the updated conversation code
      created_at: new Date().toISOString(),
    };
    // Save message to chat service
    createMessageForChat(tmpResponse)
      .catch(error => console.error("Failed to save response message:", error));

    setMessageList((prevMessageList) => [...(prevMessageList || []), tmpResponse]);

    setTimeout(() => {
      const chatContainer = document.querySelector(".ChatScrollContainer");
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 100);
    setIsTyping(false)
  };


  const chatDateFormat = (dateValue) => {
    if (!dateValue) return '';
    
    let date;
    if (dateValue instanceof Date) {
      date = new Date(dateValue);
    } else {
      // Handle UTC dates that don't have the Z suffix or timezone info
      if (typeof dateValue === 'string' && !dateValue.endsWith('Z') && !dateValue.includes('+')) {
        const utcDate = new Date(dateValue + 'Z');
        date = utcDate;
      } else {
        date = new Date(dateValue);
      }
    }
    
    // Format time in the local timezone
    const time = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);

    // Format date in the local timezone
    const day = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(date);

    return `${time} | ${day}`;
  };

  const groupConversationsByDate = (conversations) => {
    let grouped = {};

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // Sort conversations by latest date first
    conversations.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    conversations.forEach((conv, index) => {
      const convDate = new Date(conv.created_at);
      const convDay = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());

      if (convDay >= today) {
        if (!grouped['Today']) {
          grouped['Today'] = {}
          grouped['Today']['data'] = [];
          grouped['Today']['order'] = index;
        }
        grouped['Today']['data'].push(conv);
      } else if (convDay >= yesterday) {
        if (!grouped['Yesterday']) {
          grouped['Yesterday'] = {}
          grouped['Yesterday']['data'] = [];
          grouped['Yesterday']['order'] = index;
        }
        grouped['Yesterday']['data'].push(conv);
      } else if (convDay >= new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)) {
        if (!grouped['Last 7 Days']) {
          grouped['Last 7 Days'] = {}
          grouped['Last 7 Days']['data'] = [];
          grouped['Last 7 Days']['order'] = index;
        }
        grouped['Last 7 Days']['data'].push(conv);
      } else if (convDate.getFullYear() === now.getFullYear() && convDate.getMonth() === now.getMonth()) {
        if (!grouped['This Month']) {
          grouped['This Month'] = {}
          grouped['This Month']['data'] = [];
          grouped['This Month']['order'] = index;
        }
        grouped['This Month']['data'].push(conv);
      } else if (convDate.getFullYear() === now.getFullYear()) {
        if (!grouped['Current Year']) {
          grouped['Current Year'] = {}
          grouped['Current Year']['data'] = [];
          grouped['Current Year']['order'] = index;
        }
        grouped['Current Year']['data'].push(conv);
      } else {
        const year = convDate.getFullYear();
        if (!grouped[year]) {
          grouped[year] = {}
          grouped[year]['data'] = [];
          grouped[year]['order'] = index;
        }
        grouped[year]['data'].push(conv);
      }
    });
    return grouped;
  };
  const groupedChats = groupConversationsByDate(chatList);

  const filteredChats = Object.entries(groupedChats)
    .flatMap(([group, { data }]) => {
      const filteredData = Array.isArray(data)
        ? data.filter(
          (chat) =>
            chat.title.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];

      // Return the group and its filtered chats
      return filteredData.length > 0 ? [[group, filteredData]] : [];
    });

  const DocumentDrawer = ({ isOpen, onClose, documents }) => (
    <motion.div

      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '50%',
        fontSize: '0.85rem',
        height: '100%',
        backgroundColor: '#fff',
        boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
        zIndex: 1000,
        overflowY: 'auto',
      }}
    >
      <div style={{ padding: '1rem', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between' }}>
        <SubSectionTitle>Documents</SubSectionTitle>
        <Button onClick={onClose} >Close</Button>
      </div>
      <div style={{ padding: '1rem' }}>
        {documentList.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Size</TableHeader>
                <TableHeader>Masked</TableHeader>
                <TableHeader>Confidence</TableHeader>
                <TableHeader>Status</TableHeader>
              </tr>
            </thead>
            <tbody>
              {documentList.map((doc, index) => (
                <TableRow key={index}>
                  <TableCell>{doc.file_name}</TableCell>
                  <TableCell>{formatBytes(doc.file_size)}</TableCell>
                  <TableCell style={{ textAlign: 'center' }}>{doc.pii_masked === true ? (<FontAwesomeIcon icon={faCheck} />) : (<FontAwesomeIcon icon={faTimes} />)}</TableCell>
                  <TableCell style={{ textAlign: 'center' }}>{doc.classification_confidence == null ? ("-") : (doc.classification_confidence)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={'inf0'}>
                      {doc.processing_status.charAt(0).toUpperCase() + doc.processing_status.slice(1)}
                    </StatusBadge>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>


          </Table>
        ) : (
          <p>No documents available.</p>
        )}
      </div>
    </motion.div>
  );

  const goBack = useGoBack();


  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      style={{ height: "100%" }}
    >


      <FlexLayout>
        <Flex2ThirdLayout>
          {chatMode === null && (
            <>
              <div style={{ minHeight: "calc(100dvh - 235px)", display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", width: '100%' }}>
                <SubSectionTitle style={{ marginBottom: "1rem" }}>How would you like to start?</SubSectionTitle>
                <FloatingCard>
                  <FormRowFull>
                    {/* <Button secondary style={{ justifyContent: "center", padding: "1rem", border: "none" }} onClick={(e) => {
                      setChatContextandUpdateList()
                    }}> Start Conversation </Button> */}
                    <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                      {/* Attachment button with badge */}
                      {uploadedFileDetails.length > 0 && (
                        <div style={{ position: 'relative', marginRight: '8px' }}>
                          <button
                            type="button"
                            onClick={() => setShowAttachmentPanel((v) => !v)}
                            style={{
                              background: '#f0f0f0',
                              border: 'none',
                              borderRadius: '50%',
                              width: '36px',
                              height: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              cursor: 'pointer',
                              fontSize: '1.1rem',
                              padding: 0
                            }}
                            aria-label={`Show attachments (${uploadedFileDetails.length})`}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} style={{ transform: 'rotate(-45deg)', color: '#666' }} />
                            <span style={{
                              position: 'absolute',
                              top: '-4px',
                              right: '-4px',
                              background: '#007bff',
                              color: 'white',
                              borderRadius: '50%',
                              fontSize: '0.75rem',
                              width: '18px',
                              height: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              border: '2px solid #fff',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                            }}>{uploadedFileDetails.length}</span>
                          </button>
                          {/* Sliding panel for attachments */}
                          {showAttachmentPanel && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '44px',
                                left: 0,
                                minWidth: '220px',
                                background: '#fff',
                                border: '1px solid #eee',
                                borderRadius: '8px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                zIndex: 10,
                                padding: '10px',
                                animation: 'slideUp 0.2s cubic-bezier(.4,0,.2,1)',
                              }}
                            >
                              <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.95rem' }}>Attachments</div>
                              {uploadedFileDetails.map((file, idx) => (
                                <div key={idx} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '4px 0',
                                  borderBottom: idx !== uploadedFileDetails.length - 1 ? '1px solid #f0f0f0' : 'none',
                                  fontSize: '0.9rem'
                                }}>
                                  <span style={{ marginRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>{file.name}</span>
                                  <span
                                    onClick={() => removeFile(file.url)}
                                    style={{
                                      cursor: 'pointer',
                                      fontWeight: 'bold',
                                      color: '#d9534f',
                                      marginLeft: '8px',
                                      fontSize: '1.1rem'
                                    }}
                                    title="Remove"
                                  >×</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ display: 'flex', width: '100%' }}>
                        <textarea
                          ref={textareaRef}
                          value={transcribedText || ''}
                          onChange={(e) => {
                            setTranscribedText(e.target.value);
                            adjustTextareaHeight(e.target);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault(); // Prevent default to avoid new line
                              if (uploadedFileDetails.length > 0) {
                                handleIngestFiles();
                              } else if (transcribedText.trim()) {
                                handleSubmitQuery();
                              }
                            }
                          }}
                          style={{ 
                            flex: 1, 
                            marginRight: '10px',
                            resize: 'none',
                            overflow: 'hidden',
                            height: '40px',
                            padding: '8px 12px',
                            borderRadius: '20px',
                            border: 'none',
                            lineHeight: '24px',
                            fontFamily: 'inherit',
                            fontSize: '14px',
                            overflowY: 'auto'
                          }}
                          placeholder="Type your message here..."
                          rows={1}
                        />
                        <ChatButtons
                          className={transcribedText.trim() || uploadedFileDetails.length > 0 ? 'isActive' : ''}
                          onClick={() => {
                            if (uploadedFileDetails.length > 0) {
                              handleIngestFiles();
                            } else if (transcribedText.trim()) {
                              handleSubmitQuery();
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </ChatButtons>
                      </div>
                    </div>
                  </FormRowFull>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginTop: '5px' }}>
                    <div className='leftSection' style={{ display: 'flex', gap: '1rem' }}>
                      <div
                        {...getRootProps()}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "50%",
                          width: "35px",
                          height: "35px",
                          textAlign: 'center',
                          cursor: 'pointer',
                          borderStyle: "solid",
                          borderWidth: "1px",
                          borderColor: isDragActive ? '#0d0d0d1a' : '#0d0d0d1a',
                          color: isDragActive ? '#5d5d5d' : '#5d5d5d',
                        }}
                      >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p>Upload File To start Chat</p>
                        ) : (
                          <FontAwesomeIcon icon={faPlus} />
                        )}
                      </div>
                      <ChatButtons className={queryContext === 'query' ? 'isActive' : ''} onClick={() => setQueryContext('query')}><FontAwesomeIcon icon={faGlobe} /> Query</ChatButtons>
                      <ChatButtons className={queryContext === 'search' ? 'isActive' : ''} onClick={() => setQueryContext('search')}><FontAwesomeIcon icon={faSearch} /> Search</ChatButtons>
                      <ChatButtons className={queryContext === 'report' ? 'isActive' : ''} onClick={() => setQueryContext('report')}><FontAwesomeIcon icon={faBarChart} /> Report</ChatButtons>
                    </div>
                    <div className='rightSection'>
                      <ChatButtons onClick={handleVoiceRecording}>
                        <FontAwesomeIcon icon={faHeadset} />
                      </ChatButtons>
                    </div>
                  </div>
                </FloatingCard>
              </div>
            </>
          )}
          {chatMode === 'chat' && (
            <>
              <ChatWrapper>
                <ChatScrollContainer className="ChatScrollContainer" ref={chatContainerRef} >
                  {messageList?.map((conv, index) => (
                    <>
                      {conv.from === 'agent' ? (
                        <ChatSystemCard>
                          <div className={"incoming_msg_img"}> <img src="/images/logo/meclaims-icon.png" style={{ width: '25px', height: '25px' }} alt="Avatar" /> </div>
                          <div className={"received_msg"}>
                            <div className={"received_withd_msg"}>
                              <p><div className={"p-15"}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{conv.message}</ReactMarkdown>
                              </div>
                              </p>
                              <span className={"time_date"}> {chatDateFormat(conv.created_at)}</span></div>
                          </div>
                        </ChatSystemCard>
                      ) : (
                        <ChatUserCard>
                          <div className={"sent_msg"}>
                            <p><div className={"p-15"}><ReactMarkdown remarkPlugins={[remarkGfm]}>{conv.message}</ReactMarkdown></div></p>
                            <span className={"time_date"}> {chatDateFormat(conv.created_at)}</span> </div>
                        </ChatUserCard>
                      )}
                    </>
                  ))}

                  {/* Typing Loader */}
                  {isTyping && (
                    <ChatSystemCard>
                      <div className={"incoming_msg_img"}> <FontAwesomeIcon icon={faUser} /> </div>
                      <div className={"received_msg"}>
                        <div className={"received_withd_msg"}>
                          <div className="typing-loader">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                    </ChatSystemCard>
                  )}

                </ChatScrollContainer>
                <ChatBottom>
                  <FloatingCard>
                    <FormRowFull>
                      {/* <Button secondary style={{ justifyContent: "center", padding: "1rem", border: "none" }} onClick={(e) => {
                      setChatContextandUpdateList()
                    }}> Start Conversation </Button> */}
                      <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
                        {/* Attachment button with badge */}
                        {uploadedFileDetails.length > 0 && (
                          <div style={{ position: 'relative', marginRight: '8px' }}>
                            <button
                              type="button"
                              onClick={() => setShowAttachmentPanel((v) => !v)}
                              style={{
                                background: '#f0f0f0',
                                border: 'none',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                cursor: 'pointer',
                                fontSize: '1.1rem',
                                padding: 0
                              }}
                              aria-label={`Show attachments (${uploadedFileDetails.length})`}
                            >
                              <FontAwesomeIcon icon={faFileUpload} style={{ color: '#666' }} />
                              <span style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                background: '#007bff',
                                color: 'white',
                                borderRadius: '50%',
                                fontSize: '0.75rem',
                                width: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                border: '2px solid #fff',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                              }}>{uploadedFileDetails.length}</span>
                            </button>
                            {/* Sliding panel for attachments */}
                            {showAttachmentPanel && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '44px',
                                  left: 0,
                                  minWidth: '220px',
                                  background: '#fff',
                                  border: '1px solid #eee',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                  zIndex: 10,
                                  padding: '10px',
                                  animation: 'slideUp 0.2s cubic-bezier(.4,0,.2,1)',
                                }}
                              >
                                <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.95rem' }}>Attachments</div>
                                {uploadedFileDetails.map((file, idx) => (
                                  <div key={idx} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '4px 0',
                                    borderBottom: idx !== uploadedFileDetails.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    fontSize: '0.9rem'
                                  }}>
                                    <span style={{ marginRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }}>{file.name}</span>
                                    <span
                                      onClick={() => removeFile(file.url)}
                                      style={{
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        color: '#d9534f',
                                        marginLeft: '8px',
                                        fontSize: '1.1rem'
                                      }}
                                      title="Remove"
                                    >×</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <div style={{ display: 'flex', width: '100%' }}>
                          <textarea
                            ref={textareaRef2}
                            value={transcribedText || ''}
                            onChange={(e) => {
                              setTranscribedText(e.target.value);
                              adjustTextareaHeight(e.target);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault(); // Prevent default to avoid new line
                                if (uploadedFileDetails.length > 0) {
                                  handleIngestFiles();
                                } else if (transcribedText.trim()) {
                                  handleSubmitQuery();
                                }
                              }
                            }}
                            style={{ 
                              flex: 1, 
                              marginRight: '10px',
                              resize: 'none',
                              overflow: 'hidden',
                              height: '40px',
                              padding: '8px 12px',
                              borderRadius: '20px',
                              border: '1px solid #e0e0e0',
                              lineHeight: '24px',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                              overflowY: 'auto'
                            }}
                            placeholder="Type your message here..."
                            rows={1}
                          />
                          <ChatButtons 
                          className={transcribedText.trim() || uploadedFileDetails.length > 0 ? 'isActive' : ''} 
                          onClick={() => {
                            if (uploadedFileDetails.length > 0) {
                              handleIngestFiles();
                            } else if (transcribedText.trim()) {
                              handleSubmitQuery();
                            }
                            // Do nothing if there's no input text and no files
                          }}>
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </ChatButtons>
                        </div>
                      </div>
                    </FormRowFull>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',marginTop: '5px' }}>
                      <div className='leftSection' style={{ display: 'flex', gap: '1rem' }}>
                        <div
                          {...getRootProps()}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "50%",
                            width: "35px",
                            height: "35px",
                            textAlign: 'center',
                            cursor: 'pointer',
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: isDragActive ? '#0d0d0d1a' : '#0d0d0d1a',
                            color: isDragActive ? '#5d5d5d' : '#5d5d5d',
                          }}
                        >
                          <input {...getInputProps()} />
                          {isDragActive ? (
                            <p>Upload File To start Chat</p>
                          ) : (
                            <FontAwesomeIcon icon={faPlus} />
                          )}
                        </div>
                        <ChatButtons className={queryContext === 'query' ? 'isActive' : ''} onClick={() => setQueryContext('query')}><FontAwesomeIcon icon={faGlobe} /> Query</ChatButtons>
                        <ChatButtons className={queryContext === 'search' ? 'isActive' : ''} onClick={() => setQueryContext('search')}><FontAwesomeIcon icon={faSearch} /> Search</ChatButtons>
                        <ChatButtons className={queryContext === 'report' ? 'isActive' : ''} onClick={() => setQueryContext('report')}><FontAwesomeIcon icon={faBarChart} /> Report</ChatButtons>
                      </div>
                      <div className='rightSection'>
                        <ChatButtons onClick={handleVoiceRecording}>
                          <FontAwesomeIcon icon={faHeadset} />
                        </ChatButtons>
                      </div>
                    </div>
                  </FloatingCard>
                </ChatBottom>
              </ChatWrapper>
            </>
          )}

        </Flex2ThirdLayout>

        {sideBarCollapsed === true ? (
          <>
            <SubSectionTitle style={{ lineHeight: '40px', display: "flex", justifyContent: "space-between", alignItems: 'center', position: "absolute", top: '0px', right: '0px' }} >
              <FontAwesomeIcon onClick={goBack} icon={faChevronLeft} style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }} />
              {(knowledgeBaseDetail?.name && knowledgeBaseDetail?.name.length > 25) ? (
                knowledgeBaseDetail?.name.substring(0, 24) + "..."
              ) : (
                knowledgeBaseDetail?.name
              )}
              <div>
                {/* <FontAwesomeIcon
                  icon={faSearch}
                  style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }}
                  onClick={() => setIsModalOpen(true)}
                /> */}
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }}
                  onClick={() => { setSelectedChat(null); setChatMode(null) }}
                />
                <FontAwesomeIcon icon={faTableColumns} style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }}
                  onClick={() => setSideBarCollapsed(!sideBarCollapsed)} />
              </div>
            </SubSectionTitle>
          </>
        ) : (
          <Flex1ThirdLayout
            as={motion.div} // Use motion.div for animation
            initial={{ x: 0 }} // Initial position
            animate={{ x: sideBarCollapsed ? '100%' : '0%' }} // Slide out when collapsed
            transition={{ duration: 0.3, ease: 'easeInOut' }} // Smooth transition
            style={{
              position: 'relative',
              overflow: 'hidden',
              width: '33.33%', 
              paddingLeft: '20px',
              boxShadow: 'inset 0 0 10px rgba(0,0,0,0.1)',
              borderLeft: '1px solid #eee',
              backgroundColor: '#fafafa',
              display: sideBarCollapsed ? 'none' : 'block', // Hide when collapsed
              transition: 'display 0.3s ease-in-out',
            }}
          >
            <SubSectionTitle style={{ lineHeight: '40px', display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
              {(knowledgeBaseDetail?.name && knowledgeBaseDetail?.name.length > 25) ? (
                knowledgeBaseDetail?.name.substring(0, 24) + "..."
              ) : (
                knowledgeBaseDetail?.name
              )}
              <div>
                {/* <FontAwesomeIcon
                  icon={faSearch}
                  style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }}
                  onClick={() => setIsModalOpen(true)}
                /> */}
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }}
                  onClick={() => { setSelectedChat(null); setChatMode(null) }}
                />
                <FontAwesomeIcon icon={faTableColumns} style={{ fontSize: "20px", padding: "0px 15px", cursor: "pointer" }}
                  onClick={() => setSideBarCollapsed(!sideBarCollapsed)} />
              </div>
            </SubSectionTitle>

            <List maxHeight="calc(100dvh - 226px)">
              <AnimatePresence>
                {Object.entries(groupedChats)
                  .sort(([, a], [, b]) => b.order - a.order)
                  .map(([group, { data }]) => (
                    <React.Fragment key={group}>
                      <strong>{group}</strong>
                      {data.slice().reverse().map((chat, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ListItem
                            highlighted={chat.code === selectedChat?.code}
                            onClick={() => handleSelectedChat(chat)}
                          >
                            <ListItemContent>
                              <ListItemTitle>{chat.title}</ListItemTitle>
                              <ListItemDescription>
                                {(chat.summary && chat.summary.length > 42)
                                  ? chat.summary.substring(0, 41) + "..."
                                  : chat.summary}
                              </ListItemDescription>
                            </ListItemContent>
                            <ListItemMeta>
                              <ListItemTime>{chatDateFormat(chat.created_at)}</ListItemTime>
                              <ListItemStatus status={chat.status}>
                                {chat.status === 'critical'
                                  ? 'Critical'
                                  : chat.status === 'warning'
                                    ? 'Warning'
                                    : 'Normal'}
                              </ListItemStatus>
                            </ListItemMeta>
                          </ListItem>
                        </motion.div>
                      ))}
                    </React.Fragment>
                  ))}
              </AnimatePresence>
            </List>


            <AgentHeader style={{ position: 'absolute', bottom: '0px' }}>
              
              <TooltipWrapper>
                <div style={{ position: 'relative' }}>
                  <AgentAvatar>
                    <FontAwesomeIcon icon={faHexagonNodes} />
                  </AgentAvatar>
                  <QueryBadge>{knowledgeBaseDetail?.entities?.length ?? 0}</QueryBadge>
                  <Tooltip>Entities</Tooltip>
                </div>
              </TooltipWrapper>
              <TooltipWrapper onClick={() => {
                  setIsDrawerOpen(true);
                  fetchDocumentList();
                }}>
                <div style={{ position: 'relative' }}>
                  <AgentAvatar>
                    <FontAwesomeIcon icon={faFileInvoice} />
                  </AgentAvatar>
                  <QueryBadge>{uploadedFiles?.length ?? 0}</QueryBadge>
                  <Tooltip>Documents</Tooltip>
                </div>
              </TooltipWrapper>

            </AgentHeader>
          </Flex1ThirdLayout>
        )}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Filter Chats</h2>
              <input
                type="text"
                placeholder="Search by name"
                value={searchModalTerm}
                onChange={(e) => setSearchModalTerm(e.target.value)}
                style={{ marginBottom: '1rem', padding: '0.5rem', width: '100%' }}
              />
              <List>
                <List>
                  {filteredChats.map(([group, chats]) => (
                    <React.Fragment key={group}>
                      <strong>{group}</strong>
                      {chats.map((chat, index) => (
                        <ListItem
                          key={index}
                          highlighted={chat.code === selectedChat?.code}
                          onClick={() => handleSelectedChat(chat)}
                        >
                          <ListItemContent>
                            <ListItemTitle>{chat.title}</ListItemTitle>
                            <ListItemDescription>
                              {(chat.summary && chat.summary.length > 42)
                                ? chat.summary.substring(0, 41) + "..."
                                : chat.summary}
                            </ListItemDescription>
                          </ListItemContent>
                          <ListItemMeta>
                            <ListItemTime>{chatDateFormat(chat.created_at)}</ListItemTime>
                            <ListItemStatus status={chat.status}>
                              {chat.status === "critical"
                                ? "Critical"
                                : chat.status === "warning"
                                  ? "Warning"
                                  : "Normal"}
                            </ListItemStatus>
                          </ListItemMeta>
                        </ListItem>
                      ))}
                    </React.Fragment>
                  ))}
                </List>
              </List>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </FlexLayout>
      {isDrawerOpen && (
        <DocumentDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          documents={documentList}
        />
      )}

    </motion.div>
  );
};

export default KnowledgeBaseQuery;
